import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { ModuleShell, StatTile } from "@/components/intel/ModuleShell";
import { AppLayout } from "@/components/intel/Layout";
import { fetchGeoIntel, type GeoPoint } from "@/lib/intel/feeds.functions";
import { MapPin, Crosshair, Layers, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/geo")({
  head: () => ({ meta: [{ title: "GEOINT · CARIO INTEL" }] }),
  component: GeoPage,
});

function toCanvas(lat: number, lon: number) {
  // Equirectangular projection into 0-100 viewport
  const x = ((lon + 180) / 360) * 100;
  const y = ((90 - lat) / 180) * 100;
  return { x, y };
}

function GeoPage() {
  const fetcher = useServerFn(fetchGeoIntel);
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["geo-intel"],
    queryFn: () => fetcher(),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const points = useMemo(() => data ?? [], [data]);
  const [filter, setFilter] = useState<string>("all");
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const p of points) set.add(p.type);
    return ["all", ...Array.from(set).sort()];
  }, [points]);
  const visible = useMemo(
    () => (filter === "all" ? points : points.filter((p) => p.type === filter)),
    [points, filter],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected: GeoPoint | undefined = visible.find((p) => p.id === selectedId) ?? visible[0];
  useEffect(() => {
    if (selectedId && !visible.some((p) => p.id === selectedId)) setSelectedId(null);
  }, [visible, selectedId]);
  const maxRisk = visible.reduce((m, p) => Math.max(m, p.risk), 0);
  const avgConf = visible.length ? Math.round(visible.reduce((s, p) => s + p.confidence, 0) / visible.length) : 0;
  return (
    <AppLayout>
    <ModuleShell eyebrow="GEOINT" title="Geospatial intelligence correlation" description="Live real-world geo events (USGS M4.5+ seismic, NASA EONET natural events, ISS orbital track) projected onto an equirectangular world surface. Lawful public feeds only." meta={<button onClick={() => refetch()} className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs"><RefreshCw className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} />Refresh</button>}>
      <div className="grid gap-3 md:grid-cols-4">
        <StatTile label="Geo events" value={visible.length} />
        <StatTile label="Sources" value="USGS · EONET · ISS" tone="amber" />
        <StatTile label="Highest risk" value={maxRisk || "—"} tone="danger" />
        <StatTile label="Avg confidence" value={`${avgConf}%`} tone="success" />
      </div>
      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`rounded-md border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition ${filter === c ? "border-cyan/60 bg-cyan/10 text-cyan" : "border-panel-border bg-panel/40 text-muted-foreground hover:text-foreground"}`}
          >
            {c}
          </button>
        ))}
      </div>
      <div className="grid gap-3 lg:grid-cols-12">
        <div className="glass relative min-h-[380px] overflow-hidden rounded-xl p-3 sm:min-h-[560px] sm:p-4 lg:col-span-8">
          <div className="absolute inset-0 grid-bg opacity-40" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,color-mix(in_oklab,var(--cyan)_12%,transparent),transparent_45%)]" />
          <div className="relative flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan"><Layers className="h-3.5 w-3.5" /> Global correlation map · live feed</div>
          <div className="relative mt-4 h-[320px] rounded-md border border-panel-border bg-background/30 sm:h-[500px]">
            {isLoading && <div className="absolute inset-0 grid place-items-center font-mono text-xs text-muted-foreground">Polling USGS &amp; orbital telemetry…</div>}
            {!isLoading && visible.length === 0 && <div className="absolute inset-0 grid place-items-center font-mono text-xs text-muted-foreground">No geo events match this filter.</div>}
            {visible.map((p) => {
              const { x, y } = toCanvas(p.lat, p.lon);
              const isSel = (selected?.id ?? "") === p.id;
              return (
                <button key={p.id} onClick={() => setSelectedId(p.id)} className="absolute -translate-x-1/2 -translate-y-1/2 transition hover:scale-125" style={{ left: `${x}%`, top: `${y}%` }} title={`${p.name} · ${p.type} · risk ${p.risk}`}>
                  <span className={`grid h-6 w-6 place-items-center rounded-full ring-2 ${isSel ? "bg-cyan/30 ring-cyan" : p.risk > 70 ? "bg-danger/20 ring-danger/70" : p.risk > 40 ? "bg-amber/20 ring-amber/60" : "bg-success/20 ring-success/60"}`}>
                    <MapPin className="h-3 w-3" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <aside className="glass min-w-0 rounded-xl p-3 sm:p-4 lg:col-span-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Selected coordinate</div>
          {!selected && <div className="mt-3 text-sm text-muted-foreground">Awaiting live data…</div>}
          {selected && <>
          <div className="mt-3 rounded-md border border-panel-border bg-background/40 p-4">
            <Crosshair className="h-5 w-5 text-cyan" />
            <h2 className="mt-3 font-display text-xl">{selected.name}</h2>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{selected.type} · {selected.lat.toFixed(2)}, {selected.lon.toFixed(2)}</div>
            <div className="mt-2 text-xs text-muted-foreground">{selected.detail}</div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <StatTile label="Risk" value={selected.risk} tone={selected.risk > 80 ? "danger" : "amber"} />
              <StatTile label="Confidence" value={`${selected.confidence}%`} tone="success" />
            </div>
          </div>
          <div className="mt-3 space-y-2 max-h-[400px] overflow-auto">
            {visible.map((p) => <button key={p.id} onClick={() => setSelectedId(p.id)} className={`w-full rounded-md border px-3 py-2 text-left text-sm truncate transition ${selected?.id === p.id ? "border-cyan/60 bg-cyan/10" : "border-panel-border bg-background/40 hover:bg-panel/60"}`}>{p.name}<span className="float-right font-mono text-[10px] text-muted-foreground">{p.risk}</span></button>)}
          </div>
          </>}
        </aside>
      </div>
    </ModuleShell>
    </AppLayout>
  );
}